<template>
  <PComponentLoader :loading="loading">
    <div class="c-app" v-if="!loading">
      <EventManagerSidebar @createGallery="openCreateGallery"/>
      <CWrapper>
        <TheHeader/>
        <Notifier/>
        <CreateNewGalleryModal ref="createNewGalleryModal"/>
        <PublishEventModal ref="publishEventModal" @published="onPublished" @edit="onEventEdit"
                           @goToPayment="onPayment"/>
        <PublishedEventModal ref="publishedEventModal" @confirm="onConfirmPublished"/>
        <PayOnDemandModal ref="payOnDemandModal" @published="onPublished"/>
        <PayAsYouGoModal ref="payAsYouGoModal" @payed="onPayAsYouGo"/>
        <PaidEventModal ref="paidEventModal" @confirm="onConfirmPaid"/>
        <div class="c-body">
          <main class="c-main">
            <CContainer fluid>
              <router-view></router-view>
            </CContainer>
          </main>
        </div>
      </CWrapper>
    </div>
  </PComponentLoader>
</template>

<script>
import TheHeader from '../../containers/TheHeader.vue'
import Notifier from "../../components/Notifier.vue"
import {mapActions, mapGetters} from "vuex";
import EventManagerSidebar from "@/domain/eventManage/EventManagerSidebar.vue";
import MessageManager from "@/domain/core/utils/messageManager";
import CreateNewGalleryModal from "@/domain/eventGallery/components/CreateNewGalleryModal.vue";
import PayAsYouGoModal from "@/domain/events/components/PayAsYouGoModal.vue";
import PaidEventModal from "@/domain/events/components/PaidEventModal.vue";
import PublishedEventModal from "@/domain/events/components/PublishedEventModal.vue";
import PayOnDemandModal from "@/domain/events/components/PayOnDemandModal.vue";
import PublishEventModal from "@/domain/events/components/PublishEventModal.vue";
import PComponentLoader from "@/domain/core/components/PComponentLoader.vue";
import eventBus from "@/domain/core/constant/eventBus";


export default {
  name: 'EventManager',
  components: {
    PComponentLoader,
    PublishEventModal, PayOnDemandModal, PublishedEventModal,
    PaidEventModal, PayAsYouGoModal,
    CreateNewGalleryModal,
    EventManagerSidebar,
    TheHeader,
    Notifier,
  },

  data() {
    return {
      loading: true,
    }
  },
  async beforeRouteUpdate(to, from, next) {
    if (parseInt(from.params.picaServiceId) !== parseInt(to.params.picaServiceId))
      await this.fetchEventData()
    next()
  },
  async beforeMount() {
    await this.fetchEventData()
    this.registerEvent(eventBus.event.publish_data_changed, this.onPublishDataChanged)
  },
  beforeDestroy() {
    MessageManager.off()
    this.unregisterEvent(eventBus.event.publish_data_changed, this.onPublishDataChanged)
    this.emitEvent(eventBus.chat.disable, eventBus.chat.contexts.event)
  },
  computed: {
    ...mapGetters('event', [
      'isUserStatsViewer',
      'picaServiceId',
      'settingsId'
    ]),
    ...mapGetters('login', [
      'user',
    ]),
    ...mapGetters('eventWizard', [
      'isPublished',
    ])
  },
  methods: {
    ...mapActions('event', [
      'fetchPicaService',
    ]),
    ...mapActions('login', [
      'fetchConfig',
    ]),
    ...mapActions('eventWizard', [
      'setEventId',
      'fetchEventConfiguration',
      'setStepAsActive',
      'fetchStepData',
      'fetchPublishValidation',
    ]),
    ...mapActions('logoSet', {
      logoSet_setPicaServiceId: 'setPicaServiceId'
    }),
    ...mapActions('picaCode', {
      picaCode_setPicaServiceId: 'setPicaServiceId'
    }),
    ...mapActions('nStats', {
      stats_setPicaServiceId: 'setPicaServiceId',
    }),
    ...mapActions('eventGallery', {
      gallery_setPicaServiceId: 'setPicaServiceId'
    }),
    ...mapActions('edgeOrders', {
      edgeOrders_setPicaServiceId: 'setPicaServiceId'
    }),
    ...mapActions('uploader', {
      uploader_setDestination: 'setDestination',
      uploader_fetchEventStats: 'fetchEventStats',
      uploader_fetchGalleryStats: 'fetchGalleryStats',
    }),
    openCreateGallery(params) {
      if (!params) params = {}
      this.$refs.createNewGalleryModal.open(params)
    },
    async fetchEventData() {
      this.loading = true
      await this.setEventId(this.$route.params.picaServiceId)
      await this.logoSet_setPicaServiceId(this.$route.params.picaServiceId)
      await this.picaCode_setPicaServiceId(this.$route.params.picaServiceId)
      await this.stats_setPicaServiceId(this.$route.params.picaServiceId)
      await this.gallery_setPicaServiceId(this.$route.params.picaServiceId)
      await this.edgeOrders_setPicaServiceId(this.$route.params.picaServiceId)
      await this.uploader_setDestination({
        picaServiceId: this.$route.params.picaServiceId,
        galleryId: null,
      })

      await Promise.all([
        this.fetchPicaService(this.$route.params.picaServiceId),
        this.fetchEventConfiguration(),
        this.fetchConfig({
          type: 'pica_service',
          id: this.$route.params.picaServiceId
        }),
      ])

      MessageManager.setParams({
        picaServiceId: this.$route.params.picaServiceId,
        settingsId: this.settingsId,
        userId: this.user?.id
      })
      await this.emitEvent(eventBus.event.changed, this.$route.params.picaServiceId)
      await this.emitEvent(this.hasPerm('feature.chat') ? eventBus.chat.enable : eventBus.chat.disable, eventBus.chat.contexts.event);
      this.loading = false
    },
    openPayAsYouGo() {
      this.$refs.payAsYouGoModal.open()
    },
    openPublishEvent() {
      this.$refs.publishEventModal.open()
    },
    async onPayAsYouGo() {
      await this.$refs.paidEventModal.open()
      await this.fetchEventConfiguration()
      await Promise.all([
        this.fetchPicaService(this.picaServiceId),
        await this.emitEvent(eventBus.event.pay_as_you_go, this.picaServiceId),
      ])
    },
    async onPublished() {
      await this.$refs.publishedEventModal.open()
      await this.fetchEventConfiguration()
      await Promise.all([
        this.fetchPicaService(this.picaServiceId),
        await this.emitEvent(eventBus.event.published, this.picaServiceId),
      ])
    },
    async routeToOverview() {
      if (this.$route.name === 'picaServiceRedirect') return
      await this.$router.push({name: 'picaServiceRedirect', params: {picaServiceId: this.picaServiceId}})
    },
    onEventEdit() {
      if (this.$route.name === 'eventWizard.date_and_name') return
      this.fetchStepData('date_and_name')
      this.$router.push({name: 'eventWizard.date_and_name', params: {picaServiceId: this.picaServiceId}})
    },
    onPayment(options) {
      this.$refs.payOnDemandModal.open(options)
    },
    async onConfirmPublished() {
      await this.$nextTick()
      await this.delay(500)
      await this.routeToOverview()
      this.$refs.publishedEventModal.close()
    },
    async onConfirmPaid() {
      if (this.$route.name === 'eventWizard.gallery.browse_photo') {
        this.$router.go(0)
      }
    },

    async onPublishDataChanged() {
      if (!this.isPublished) {
        await this.fetchPublishValidation()
      }
    }
  }
}
</script>
